import type React from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
  Ban,
  Check,
  CheckCircle,
  CheckCircle2,
  ChevronDown,
  Clock,
  DollarSign,
  Download,
  FileCheck,
  Loader2,
  Lock,
  Upload,
  Users,
  X,
  XCircle,
} from "lucide-react";
import type {
  SelectionMode,
  SelectionType,
} from "@/hooks/CreateSalaryHooks/useEmployeeSelection";

export type { SelectionMode, SelectionType };

export interface SelectionActionProps {
  isAllBranchUsersSelected?: boolean;
  selectedCount?: number;
  selectionType?: SelectionType;
  isSelectingUsers?: boolean;
  onSelectUsersByMode?: (mode: SelectionMode) => void;
  onToggleSelectAllBranchUsers?: () => void;
}

const SelectBranchUsersButton = ({
  isAllBranchUsersSelected,
  selectedCount = 0,
  selectionType = "none",
  isSelectingUsers = false,
  onSelectUsersByMode,
  onToggleSelectAllBranchUsers,
}: SelectionActionProps) => {
  const handleMode = (mode: SelectionMode) => {
    if (isSelectingUsers) return;
    if (onSelectUsersByMode) {
      onSelectUsersByMode(mode);
    } else if (onToggleSelectAllBranchUsers) {
      onToggleSelectAllBranchUsers();
    }
  };

  const isSelected = isAllBranchUsersSelected || selectedCount > 0;

  const getButtonText = () => {
    if (isSelectingUsers) {
      return "Selecting Users...";
    }
    if (selectionType === "all" || isAllBranchUsersSelected) {
      return selectedCount > 0 ? `All Users Selected (${selectedCount})` : "All Branch Users Selected";
    }
    if (selectionType === "configured") {
      return `Salary Configured Users (${selectedCount})`;
    }
    if (selectionType === "not_configured") {
      return `Salary Not Configured (${selectedCount})`;
    }
    if (selectedCount > 0) {
      return `${selectedCount} Users Selected`;
    }
    return "Select Users";
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild disabled={isSelectingUsers}>
        <Button
          type="button"
          disabled={isSelectingUsers}
          variant={isSelected ? "default" : "outline"}
          size="sm"
          className={`gap-2 font-semibold h-9 rounded-lg transition-all cursor-pointer shadow-2xs ${
            isSelected
              ? "bg-primary text-white shadow-xs"
              : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50"
          }`}
        >
          {isSelectingUsers ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : isSelected ? (
            <Check className="w-4 h-4" />
          ) : (
            <Users className="w-4 h-4" />
          )}
          <span>{getButtonText()}</span>
          <ChevronDown className="w-3.5 h-3.5 opacity-70 ml-0.5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-60 rounded-xl shadow-lg border-slate-200 p-1.5 z-50 bg-white">
        <DropdownMenuLabel className="text-[11px] font-bold text-slate-500 uppercase px-2 py-1">
          Select Users by
        </DropdownMenuLabel>

        <DropdownMenuItem
          onClick={() => handleMode("all")}
          disabled={isSelectingUsers}
          className="flex items-center gap-2.5 px-2.5 py-2 text-xs font-semibold cursor-pointer rounded-lg hover:bg-slate-100 text-slate-700 data-[highlighted]:bg-slate-100"
        >
          <Users className="w-4 h-4 text-blue-600 shrink-0" />
          <div className="flex flex-col">
            <span className="text-slate-800 font-semibold">1. Select All Users</span>
            <span className="text-[10px] text-muted-foreground font-normal">All employees in branch / site</span>
          </div>
        </DropdownMenuItem>

        <DropdownMenuItem
          onClick={() => handleMode("configured")}
          disabled={isSelectingUsers}
          className="flex items-center gap-2.5 px-2.5 py-2 text-xs font-semibold cursor-pointer rounded-lg hover:bg-emerald-50 text-emerald-800 data-[highlighted]:bg-emerald-50"
        >
          <CheckCircle className="w-4 h-4 text-emerald-600 shrink-0" />
          <div className="flex flex-col">
            <span className="text-emerald-800 font-semibold">2. Salary Configured Users</span>
            <span className="text-[10px] text-emerald-600/80 font-normal">Employees with salary configured</span>
          </div>
        </DropdownMenuItem>

        <DropdownMenuItem
          onClick={() => handleMode("not_configured")}
          disabled={isSelectingUsers}
          className="flex items-center gap-2.5 px-2.5 py-2 text-xs font-semibold cursor-pointer rounded-lg hover:bg-rose-50 text-rose-800 data-[highlighted]:bg-rose-50"
        >
          <XCircle className="w-4 h-4 text-rose-600 shrink-0" />
          <div className="flex flex-col">
            <span className="text-rose-800 font-semibold">3. Salary Not Configured</span>
            <span className="text-[10px] text-rose-600/80 font-normal">Employees without salary configured</span>
          </div>
        </DropdownMenuItem>

        {isSelected && (
          <>
            <DropdownMenuSeparator className="my-1" />
            <DropdownMenuItem
              onClick={() => handleMode("clear")}
              disabled={isSelectingUsers}
              className="flex items-center gap-2.5 px-2.5 py-1.5 text-xs font-semibold cursor-pointer rounded-lg text-slate-600 hover:bg-slate-100 data-[highlighted]:bg-slate-100"
            >
              <Ban className="w-3.5 h-3.5 text-slate-400 shrink-0" />
              <span>Deselect All</span>
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export interface EmployeeToolbarProps extends SelectionActionProps {
  selectedEmployeeIds: Set<string | number>;
  selectedFile: File | null;
  isDownloading: boolean;
  isUploading: boolean;
  canDownloadTemplate: boolean;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  onDownloadTemplate: () => void;
  onFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onClearFile: () => void;
  onUploadFile: () => void;
}

export const EmployeeToolbar = ({
  selectedEmployeeIds,
  selectedFile,
  isAllBranchUsersSelected,
  selectedCount,
  selectionType,
  isSelectingUsers,
  isDownloading,
  isUploading,
  canDownloadTemplate,
  fileInputRef,
  onSelectUsersByMode,
  onToggleSelectAllBranchUsers,
  onDownloadTemplate,
  onFileChange,
  onClearFile,
  onUploadFile,
}: EmployeeToolbarProps) => (
  <div className="flex flex-wrap items-center gap-2">
    <input
      ref={fileInputRef}
      type="file"
      accept=".xlsx, .xls, .csv"
      onChange={onFileChange}
      className="hidden"
    />

    <SelectBranchUsersButton
      isAllBranchUsersSelected={isAllBranchUsersSelected}
      selectedCount={selectedCount ?? selectedEmployeeIds.size}
      selectionType={selectionType}
      isSelectingUsers={isSelectingUsers}
      onSelectUsersByMode={onSelectUsersByMode}
      onToggleSelectAllBranchUsers={onToggleSelectAllBranchUsers}
    />

    <Button
      type="button"
      size="sm"
      variant="outline"
      onClick={onDownloadTemplate}
      disabled={!canDownloadTemplate || isDownloading}
      className={`gap-2 font-semibold h-9 rounded-lg transition-all shadow-2xs ${
        canDownloadTemplate
          ? "bg-white hover:bg-blue-50 text-blue-700 border-blue-200"
          : "bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed"
      }`}
    >
      <Download className="w-4 h-4" />
      {isDownloading
        ? "Downloading..."
        : selectedEmployeeIds.size > 0 && !isAllBranchUsersSelected
          ? "Download Heads Template (Selected)"
          : "Download Heads Template"}
    </Button>

    {selectedFile ? (
      <div className="flex items-center gap-1.5 bg-emerald-50 border border-emerald-200 rounded-lg px-2.5 py-1">
        <span className="text-xs font-semibold text-emerald-800 flex items-center gap-1 max-w-[180px] truncate">
          <FileCheck className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
          {selectedFile.name}
        </span>
        <button
          type="button"
          onClick={onClearFile}
          className="text-slate-400 hover:text-slate-600 p-0.5 cursor-pointer"
          title="Remove file"
        >
          <X className="w-3.5 h-3.5" />
        </button>
        <Button
          type="button"
          size="sm"
          onClick={onUploadFile}
          disabled={isUploading}
          className="h-7 px-3 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-xs rounded-lg gap-1.5 ml-1 cursor-pointer"
        >
          <Upload className="w-3.5 h-3.5" />
          {isUploading ? "Uploading..." : "Upload"}
        </Button>
      </div>
    ) : (
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={() => fileInputRef.current?.click()}
        className="gap-2 font-semibold h-9 rounded-lg transition-all shadow-2xs bg-emerald-600 text-white hover:bg-emerald-700 border-emerald-600 cursor-pointer"
        title="Upload Salary Heads File"
      >
        <Upload className="w-4 h-4" />
        Upload Salary Heads
      </Button>
    )}
  </div>
);

const SelectAttendanceUsersButton = ({
  isAllBranchUsersSelected,
  selectedCount = 0,
  selectionType = "none",
  isSelectingUsers = false,
  onSelectUsersByMode,
  onToggleSelectAllBranchUsers,
}: SelectionActionProps) => {
  const handleMode = (mode: SelectionMode) => {
    if (isSelectingUsers) return;
    if (onSelectUsersByMode) {
      onSelectUsersByMode(mode);
    } else if (onToggleSelectAllBranchUsers) {
      onToggleSelectAllBranchUsers();
    }
  };

  const isSelected = isAllBranchUsersSelected || selectedCount > 0;

  const getButtonText = () => {
    if (isSelectingUsers) {
      return "Selecting Users...";
    }
    if (selectionType === "all" || isAllBranchUsersSelected) {
      return selectedCount > 0 ? `All Eligible Users (${selectedCount})` : "All Branch Users Selected";
    }
    if (selectionType === "not_locked") {
      return `Attendance Not Locked (${selectedCount})`;
    }
    if (selectionType === "locked_pending_salary") {
      return `Attendance Locked (${selectedCount})`;
    }
    if (selectedCount > 0) {
      return `${selectedCount} Users Selected`;
    }
    return "Select Users";
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild disabled={isSelectingUsers}>
        <Button
          type="button"
          disabled={isSelectingUsers}
          variant={isSelected ? "default" : "outline"}
          size="sm"
          className={`gap-2 font-semibold h-9 rounded-lg transition-all cursor-pointer shadow-2xs ${
            isSelected
              ? "bg-primary text-white shadow-xs"
              : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50"
          }`}
        >
          {isSelectingUsers ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : isSelected ? (
            <Check className="w-4 h-4" />
          ) : (
            <Users className="w-4 h-4" />
          )}
          <span>{getButtonText()}</span>
          <ChevronDown className="w-3.5 h-3.5 opacity-70 ml-0.5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-64 rounded-xl shadow-lg border-slate-200 p-1.5 z-50 bg-white">
        <DropdownMenuLabel className="text-[11px] font-bold text-slate-500 uppercase px-2 py-1">
          Select Users by
        </DropdownMenuLabel>

        <DropdownMenuItem
          onClick={() => handleMode("all")}
          disabled={isSelectingUsers}
          className="flex items-center gap-2.5 px-2.5 py-2 text-xs font-semibold cursor-pointer rounded-lg hover:bg-slate-100 text-slate-700 data-[highlighted]:bg-slate-100"
        >
          <Users className="w-4 h-4 text-blue-600 shrink-0" />
          <div className="flex flex-col">
            <span className="text-slate-800 font-semibold">1. Select All Eligible Users</span>
            <span className="text-[10px] text-muted-foreground font-normal">All branch users without generated salary</span>
          </div>
        </DropdownMenuItem>

        <DropdownMenuItem
          onClick={() => handleMode("not_locked")}
          disabled={isSelectingUsers}
          className="flex items-center gap-2.5 px-2.5 py-2 text-xs font-semibold cursor-pointer rounded-lg hover:bg-amber-50 text-amber-800 data-[highlighted]:bg-amber-50"
        >
          <Clock className="w-4 h-4 text-amber-600 shrink-0" />
          <div className="flex flex-col">
            <span className="text-amber-800 font-semibold">2. Attendance Not Locked</span>
            <span className="text-[10px] text-amber-600/80 font-normal">Employees with pending attendance lock</span>
          </div>
        </DropdownMenuItem>

        <DropdownMenuItem
          onClick={() => handleMode("locked_pending_salary")}
          disabled={isSelectingUsers}
          className="flex items-center gap-2.5 px-2.5 py-2 text-xs font-semibold cursor-pointer rounded-lg hover:bg-emerald-50 text-emerald-800 data-[highlighted]:bg-emerald-50"
        >
          <CheckCircle className="w-4 h-4 text-emerald-600 shrink-0" />
          <div className="flex flex-col">
            <span className="text-emerald-800 font-semibold">3. Attendance Locked (Salary Pending)</span>
            <span className="text-[10px] text-emerald-600/80 font-normal">Locked attendance ready for payroll</span>
          </div>
        </DropdownMenuItem>

        {isSelected && (
          <>
            <DropdownMenuSeparator className="my-1" />
            <DropdownMenuItem
              onClick={() => handleMode("clear")}
              disabled={isSelectingUsers}
              className="flex items-center gap-2.5 px-2.5 py-1.5 text-xs font-semibold cursor-pointer rounded-lg text-slate-600 hover:bg-slate-100 data-[highlighted]:bg-slate-100"
            >
              <Ban className="w-3.5 h-3.5 text-slate-400 shrink-0" />
              <span>Deselect All</span>
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

interface AttendanceToolbarProps extends SelectionActionProps {
  selectedCount?: number;
  isAttendanceLocked: boolean;
  isLockingAttendance: boolean;
  isPastYear?: boolean;
  onLockAttendance: () => void;
}

export const AttendanceToolbar = ({
  isAllBranchUsersSelected,
  selectedCount,
  selectionType,
  isSelectingUsers,
  isAttendanceLocked,
  isLockingAttendance,
  isPastYear = false,
  onSelectUsersByMode,
  onToggleSelectAllBranchUsers,
  onLockAttendance,
}: AttendanceToolbarProps) => (
  <div className="flex flex-wrap items-center gap-2">
    <SelectAttendanceUsersButton
      isAllBranchUsersSelected={isAllBranchUsersSelected}
      selectedCount={selectedCount}
      selectionType={selectionType}
      isSelectingUsers={isSelectingUsers}
      onSelectUsersByMode={onSelectUsersByMode}
      onToggleSelectAllBranchUsers={onToggleSelectAllBranchUsers}
    />

    {isAttendanceLocked ? (
      <div className="flex items-center gap-2 bg-emerald-50 text-emerald-700 border border-emerald-200 px-3.5 py-1.5 rounded-lg text-xs font-semibold shadow-2xs">
        <Lock className="w-4 h-4 text-emerald-600" />
        <span>Attendance Already Locked</span>
      </div>
    ) : (
      <Button
        type="button"
        size="sm"
        onClick={onLockAttendance}
        disabled={isLockingAttendance || isPastYear}
        className={`gap-2 font-semibold h-9 rounded-lg shadow-2xs transition-all ${
          isPastYear
            ? "bg-slate-200 text-slate-400 border-slate-200 cursor-not-allowed hover:bg-slate-200"
            : "bg-rose-600 hover:bg-rose-700 text-white cursor-pointer"
        }`}
        title={isPastYear ? "Cannot lock attendance for past year" : undefined}
      >
        {isLockingAttendance ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <Lock className="w-4 h-4" />
        )}
        {isLockingAttendance ? "Locking..." : "Lock Attendance"}
      </Button>
    )}
  </div>
);

interface SalaryCreateActionProps {
  salarySubTab: "users" | "errors";
  readyUsersCount: number;
  isCreatingSalary: boolean;
  onOpenConfirm: () => void;
}

export const SalaryCreateAction = ({
  salarySubTab,
  readyUsersCount,
  isCreatingSalary,
  onOpenConfirm,
}: SalaryCreateActionProps) => {
  if (salarySubTab !== "users" || readyUsersCount === 0) return null;

  return (
    <Button
      type="button"
      onClick={onOpenConfirm}
      disabled={isCreatingSalary}
      className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-xs px-4 py-2 rounded-lg flex items-center gap-2 shadow-xs transition-all cursor-pointer"
    >
      {isCreatingSalary ? (
        <Loader2 className="w-4 h-4 animate-spin text-white" />
      ) : (
        <DollarSign className="w-4 h-4 text-white" />
      )}
      {isCreatingSalary ? "Creating Salary..." : "Create Salary"}
    </Button>
  );
};

export const SelectionSummary = ({
  selectedEmployeeIds,
  isAllBranchUsersSelected,
}: {
  selectedEmployeeIds: Set<string | number>;
  isAllBranchUsersSelected: boolean;
}) => {
  if (selectedEmployeeIds.size === 0 && !isAllBranchUsersSelected) return null;

  return (
    <div className="flex items-center gap-2 bg-primary/10 text-primary border border-primary/20 px-3 py-1.5 rounded-lg text-xs font-semibold">
      <CheckCircle2 className="w-4 h-4" />
      <span>
        {isAllBranchUsersSelected
          ? "All Branch Users Selected"
          : `${selectedEmployeeIds.size} Employee${
              selectedEmployeeIds.size > 1 ? "s" : ""
            } Selected`}
      </span>
    </div>
  );
};
